import Dropzone from "dropzone";
import { Controller } from "stimulus";
import { DirectUpload } from "@rails/activestorage";
import StimulusReflex from 'stimulus_reflex'
import {
    getMetaValue,
    toArray,
    findElement,
    removeElement,
    insertAfter
} from "@/helpers";
import $ from "@/packs/jquery";

export default class extends Controller {
    static targets = ["input"];

    connect() {
        StimulusReflex.register(this)
        this.dropZone = createDropZone(this);
        this.hideFileInput();
        this.bindEvents();
        Dropzone.autoDiscover = false; // necessary quirk for Dropzone error in console
    }

    // Private
    hideFileInput() {
        this.inputTarget.disabled = true;
        this.inputTarget.style.display = "none";
    }

    bindEvents() {
        this.dropZone.on("addedfiles", files => {
            const acceptedFiles = Array.from(files).filter(file => file.accepted);
            if (acceptedFiles.length > 0) {
                createDirectUploadController(this, acceptedFiles).start();
            }
        });

        this.dropZone.on("successmultiple", file => {
            console.log('SUCCESS MULTIPLE')
        });

        this.dropZone.on("removedfile", file => {
            file.controller && removeElement(file.controller.hiddenInput);
        });

        this.dropZone.on("canceled", file => {
            file.controller && file.controller.xhr.abort();
        });
    }

    get headers() {
        return { "X-CSRF-Token": getMetaValue("csrf-token") };
    }

    get url() {
        return this.inputTarget.getAttribute("data-direct-upload-url");
    }

    get maxFiles() {
        return this.data.get("maxFiles") || 100;
    }

    get maxFileSize() {
        return this.data.get("maxFileSize") || 256;
    }

    get acceptedFiles() {
        return this.data.get("acceptedFiles");
    }

    get addRemoveLinks() {
        return this.data.get("addRemoveLinks") || true;
    }
}

class DirectUploadController {
    constructor(source, files) {
        this.stimulus_controller = source;
        this.source = source;
        this.files = Array.isArray(files) ? files : [files];
        this.uploadPromises = [];
    }

    start() {
        this.files.forEach(file => {
            file.controller = this;
            const hiddenInput = this.createHiddenInput();
            const directUpload = createDirectUpload(file, this.source.url, this);

            const uploadPromise = new Promise((resolve, reject) => {
                directUpload.create((error, attributes) => {
                    if (error) {
                        removeElement(hiddenInput);
                        this.emitDropzoneError(file, error);
                        reject(error);
                    } else {
                        hiddenInput.value = attributes.signed_id;
                        this.emitDropzoneSuccess(file);
                        resolve(attributes);
                    }
                });
            });

            this.uploadPromises.push(uploadPromise);
        });

        // Wait for all uploads to complete and add a delay
        Promise.all(this.uploadPromises)
            .then(() => {
                // Add a delay to ensure S3 consistency
                return new Promise(resolve => setTimeout(resolve, 500));
            })
            .then(() => {
                // All files uploaded and delay completed
                this.source.stimulate(
                    this.source.data.get("event"),
                    this.source.element
                );
            })
            .catch(error => {
                console.error('Upload failed:', error);
            });
    }

    createHiddenInput() {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = this.source.inputTarget.name;
        insertAfter(input, this.source.inputTarget);
        return input;
    }

    directUploadWillStoreFileWithXHR(xhr) {
        this.bindProgressEvent(xhr);
        this.emitDropzoneUploading();
    }

    bindProgressEvent(xhr) {
        this.xhr = xhr;
        this.xhr.upload.addEventListener("progress", event =>
            this.uploadRequestDidProgress(event)
        );
    }

    uploadRequestDidProgress(event) {
        const progress = (event.loaded / event.total) * 100;
    }

    emitDropzoneUploading() {
        this.files.forEach(file => {
            file.status = Dropzone.UPLOADING;
            this.source.dropZone.emit("processing", file);
        });
    }

    emitDropzoneError(file, error) {
        file.status = Dropzone.ERROR;
        this.source.dropZone.emit("error", file, error);
        this.source.dropZone.emit("complete", file);
    }

    emitDropzoneSuccess(file) {
        file.status = Dropzone.SUCCESS;
        this.source.dropZone.emit("success", file);
        this.source.dropZone.emit("complete", file);
    }
}

function createDirectUploadController(source, files) {
    return new DirectUploadController(source, files);
}

function createDirectUpload(file, url, controller) {
    let new_file_name = controller.stimulus_controller.element.getAttribute('data-reflex-dataset') + '---' + file.name;
    let new_file = new File([file], new_file_name, {
        type: file.type,
        lastModified: file.lastModified,
    });
    return new DirectUpload(new_file, url, controller);
}

function createDropZone(controller) {
    return new Dropzone(controller.element, {
        url: controller.url,
        headers: controller.headers,
        maxFiles: controller.maxFiles,
        maxFilesize: controller.maxFileSize,
        acceptedFiles: controller.acceptedFiles,
        autoQueue: false
    });
}
